import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { NavigationLinkItemType } from 'src/domains/players/state/specialEventsState/HeaderNavigationLink';
import { LinksScrollableWrapper } from 'src/domains/layouts/webview/modules/layoutSkeleton/Header/linksScrollableWrapper/LinksScrollableWrapper';
import { HeaderLinksType } from 'src/domains/layouts/config/features/types';
import {
    HeaderMainLinkWrapper,
    HeaderStyledLink,
    NewsIconWrapper,
    HeaderStyledStaticLink,
    SpecialsIconWrapper,
    HeaderStyledStaticOnClick,
    StatsIconWrapper,
    BingoIconWrapper,
} from 'src/domains/layouts/webview/modules/layoutSkeleton/Header/headerMainLinks/HeaderMainLinks.style';
import { isMainViewRouteEq, RouteViewType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { UniverseComponent } from 'src/domains/common/universes';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useCommon } from 'src/domains/common/Common';
import { FeatureState } from 'src/domains/layouts/config/features/featureState/FeatureState';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { HeaderLinkType } from 'src/domains/common/configFeatures/ConfigFeatures';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { AppPlayersState } from 'src/domains/players/state/AppPlayers.state';

interface HeaderItemType {
    icon: (isActive?: boolean) => JSX.Element;
    label: string | React.ReactNode;
    onSidebar?: boolean;
    isHidden?: boolean;
    displayOrder?: number | null;
    action:
        | {
              type: 'redirect';
              to: RouteViewType | undefined;
              isActive?: (route: string, id?: string) => boolean;
              dataTest?: string;
          }
        | {
              type: 'redirect-outside';
              href: string;
              dataTest: string;
          }
        | {
              type: 'onclick';
              onClick: () => void;
              dataTest: string;
          };
}

interface PropsType {
    className?: string;
    onSidebar?: boolean;
    currentView: RouteViewType;
    languageWidth: number;
}

export const HeaderMainLinks = observer('HeaderMainLinks', (props: PropsType) => {
    const common = useCommon();
    const appPlayersState = AppPlayersState.get(common);
    const appLayoutsState = AppLayoutsState.get(common);
    const { overlay } = appLayoutsState;
    const configState = ConfigComponents.get(common).config;
    const featureState = FeatureState.get(common);
    const breakpointsState = BreakpointsState.get(common);

    const { isHamburgerMenuOpen } = overlay;

    const getSpecialEventsHeaders = (): Array<HeaderItemType> => {
        const { specialEvents } = appPlayersState;
        const { validatedLinks, getParams } = specialEvents.headerNavigationLinks;

        return validatedLinks.map((link: NavigationLinkItemType) => {
            const isDirectLink = link.action_template === 'direct-link';
            const linkUrl = link.cta_url;
            const redirectTo = getParams(link);
            const redirectToName = redirectTo?.name ?? 'redirect-link';

            if (isDirectLink && linkUrl !== '' && linkUrl !== undefined && linkUrl !== null) {
                return {
                    icon: (): JSX.Element => <SpecialsIconWrapper source={link.img_url} />,
                    label: link.title,
                    onSidebar: true,
                    displayOrder: link.display_order,
                    action: {
                        type: 'redirect-outside',
                        href: linkUrl,
                        dataTest: `${link.title}-header-special`,
                    },
                };
            } else {
                const link2: HeaderItemType = {
                    icon: (): JSX.Element =>
                        link.sport === 'premier-league' ? (
                            //until LT-944 is not fixed
                            <StatsIconWrapper />
                        ) : (
                            <SpecialsIconWrapper source={link.img_url} />
                        ),
                    label: link.title,
                    onSidebar: true,
                    displayOrder: link.display_order,
                    action: {
                        type: 'redirect',
                        to: redirectTo,
                        dataTest: `${link.title}-${redirectToName}`,
                    },
                };

                return link2;
            }
        });
    };

    const findHeaderLink = (headerItem: HeaderLinksType): HeaderItemType => {
        switch (headerItem) {
            case 'football': {
                return {
                    icon: (): JSX.Element => (
                        <UniverseComponent>
                            {({ headerMainLinksIcons }): React.ReactElement => <headerMainLinksIcons.footballIcon />}
                        </UniverseComponent>
                    ),
                    label: (
                        <I18n
                            langKey='header.football.label'
                            defaultText='football'
                        />
                    ),
                    onSidebar: true,
                    action: {
                        type: 'redirect',
                        to: { name: 'sport', nameType: 'regular', id: 'football' },
                        isActive: (route: string): boolean => ['football'].includes(route),
                    },
                };
            }
            case 'horse racing': {
                return {
                    icon: (): JSX.Element => (
                        <UniverseComponent>
                            {({ headerMainLinksIcons }): React.ReactElement => <headerMainLinksIcons.horseRacingIcon />}
                        </UniverseComponent>
                    ),
                    label: (
                        <I18n
                            langKey='header.horse-racing.label'
                            defaultText='horse racing'
                        />
                    ),
                    onSidebar: true,
                    action: {
                        type: 'redirect',
                        to: { name: 'sport', nameType: 'regular', id: 'horseracing' },
                        isActive: (route: string): boolean => ['horseracing'].includes(route),
                    },
                };
            }
            case 'sports': {
                return {
                    icon: (): JSX.Element => (
                        <UniverseComponent>
                            {({ headerMainLinksIcons }): React.ReactElement => <headerMainLinksIcons.sportsIcon />}
                        </UniverseComponent>
                    ),
                    label: (
                        <I18n
                            langKey='header.sports.label'
                            defaultText='sports'
                        />
                    ),
                    onSidebar: true,
                    action: {
                        type: 'redirect',
                        to: { name: 'homepage' },
                        isActive: (route: string, id?: string): boolean => {
                            const routesWithoutId = [
                                'event',
                                'sport-special',
                                'promoted-events',
                                'competition',
                                'racecard',
                            ];

                            if (routesWithoutId.includes(route)) {
                                return true;
                            }

                            if (route === 'sport' && id) {
                                const { headerLinks } = configState;

                                const excludedLinks: Record<string, HeaderLinkType> = {
                                    horseracing: 'horse racing',
                                    football: 'football',
                                };

                                const excludedLink = excludedLinks[id];
                                return !(excludedLink && headerLinks.includes(excludedLink));
                            }

                            return false;
                        },
                    },
                };
            }
            case 'in-play': {
                return {
                    icon: (): JSX.Element => (
                        <UniverseComponent>
                            {({ headerMainLinksIcons }): React.ReactElement => <headerMainLinksIcons.inPlayIcon />}
                        </UniverseComponent>
                    ),
                    label: (
                        <I18n
                            langKey='header.in-play.label'
                            defaultText='in play'
                        />
                    ),
                    onSidebar: true,
                    action: {
                        type: 'redirect',
                        to: { name: 'inplay' },
                    },
                };
            }
            case 'virtuals': {
                return {
                    icon: (): JSX.Element => (
                        <UniverseComponent>
                            {({ headerMainLinksIcons }): React.ReactElement => <headerMainLinksIcons.virtualsIcon />}
                        </UniverseComponent>
                    ),
                    label: (
                        <I18n
                            langKey='header.virtual-sports.label'
                            defaultText='virtuals'
                        />
                    ),
                    onSidebar: true,
                    action: {
                        type: 'redirect',
                        to: {
                            name: 'virtuals',
                        },
                    },
                };
            }
            case 'casino': {
                return {
                    icon: (): JSX.Element => (
                        <UniverseComponent>
                            {({ headerMainLinksIcons }): React.ReactElement => <headerMainLinksIcons.casinoIcon />}
                        </UniverseComponent>
                    ),
                    label: (
                        <I18n
                            langKey='header.casino.label'
                            defaultText='casino'
                        />
                    ),
                    onSidebar: true,
                    action: {
                        type: 'redirect',
                        to: {
                            name: 'casino',
                            tabId: null,
                            game: null,
                        },
                    },
                };
            }
            case 'live-casino': {
                return {
                    icon: (): JSX.Element => (
                        <UniverseComponent>
                            {({ headerMainLinksIcons }): React.ReactElement => <headerMainLinksIcons.liveCasinoIcon />}
                        </UniverseComponent>
                    ),
                    label: (
                        <I18n
                            langKey='header.live-casino.label'
                            defaultText='Live casino'
                        />
                    ),
                    onSidebar: true,
                    action: {
                        type: 'redirect',
                        to: {
                            name: 'live-casino',
                            tabId: null,
                        },
                    },
                };
            }
            case 'bingo': {
                return {
                    icon: (): JSX.Element => <BingoIconWrapper />,
                    label: (
                        <I18n
                            langKey='header.bingo.label'
                            defaultText='Bingo'
                        />
                    ),
                    onSidebar: true,
                    action: {
                        type: 'redirect',
                        to: {
                            name: 'casino',
                            game: 'bingo',
                        },
                    },
                };
            }
            case 'star-news': {
                return {
                    icon: (): JSX.Element => <NewsIconWrapper />,
                    label: (
                        <I18n
                            langKey='header.news.label'
                            defaultText='news'
                        />
                    ),
                    action: {
                        type: 'redirect-outside',
                        href: 'https://www.starsportsbet.co.uk/',
                        dataTest: 'news',
                    },
                };
            }
            case 'mcbookie-blog': {
                return {
                    icon: (): JSX.Element => <NewsIconWrapper />,
                    label: (
                        <I18n
                            langKey='header.blog.label:mcbookie'
                            defaultText='blog'
                        />
                    ),
                    action: {
                        type: 'redirect-outside',
                        href: 'https://mcbookieblog.com/',
                        dataTest: 'news',
                    },
                };
            }
            case 'dragonbet-news': {
                return {
                    icon: (): JSX.Element => <NewsIconWrapper />,
                    label: (
                        <I18n
                            langKey='header.news.label:dragonbet'
                            defaultText='News'
                        />
                    ),
                    action: {
                        type: 'redirect-outside',
                        href: 'https://www.dragonsports.co.uk/',
                        dataTest: 'news',
                    },
                };
            }
        }
    };

    const getHeaderItemsDefault = (headerItems: Array<HeaderLinksType>): Array<HeaderItemType> => {
        const { navigationMobileLinks } = configState;
        const isLargeDesktop = breakpointsState.largeDesktop.isBiggerOrEq;
        const specialEventsArray = getSpecialEventsHeaders();
        const links: Array<HeaderItemType> = [];
        const headerLinks = (): void => {
            headerItems.map((headerItem) => {
                links.push(findHeaderLink(headerItem));
            });
        };
        const specialLinks = (): void => {
            specialEventsArray.map((specialLink: HeaderItemType) => {
                if (specialLink.displayOrder !== undefined && specialLink.displayOrder !== null) {
                    links.splice(specialLink.displayOrder - 1, 0, specialLink);
                } else {
                    links.push(specialLink);
                }
            });
        };

        if (isLargeDesktop === true || navigationMobileLinks === 'header-links') {
            headerLinks();
            specialLinks();
        } else {
            specialLinks();
        }

        return links;
    };

    const getHeadersConfig = (): HeaderLinksType[] => {
        const { headerLinks, casinoPublishedCollections } = configState;
        const headerItems = headerLinks.filter((item) => {
            if (item === 'casino' || item === 'live-casino' || item === 'virtuals' || item === 'bingo') {
                if (featureState.allowCasinoInIOSWrapperNew === false) {
                    return false;
                }
                return casinoPublishedCollections.includes(item);
            }
            return true;
        });
        return headerItems;
    };

    const getHeaderItems = (): Array<HeaderItemType> => {
        return getHeaderItemsDefault(getHeadersConfig());
    };

    const isActive = (link: HeaderItemType, currentView: RouteViewType): boolean => {
        if (link.action.type === 'onclick' || link.action.type === 'redirect-outside') {
            return false;
        }

        if (link.action.to?.name === 'casino' && link.action.to.game === 'bingo') {
            return false;
        }
        const sportIdParam = currentView.name === 'sport' ? currentView.id : undefined;
        const thirdCase: boolean =
            link.action.isActive !== undefined && link.action.isActive(currentView.name, sportIdParam);
        if (thirdCase) {
            return true;
        }

        const to = link.action.to;
        if (to === undefined) {
            return false;
        }

        return isMainViewRouteEq(to, currentView);
    };

    const fireGtmTag = (route: string | undefined): undefined => {
        const googleTagManager = GoogleTagManagerState.get(common);
        googleTagManager.categoryViewTag(route);
        return undefined;
    };

    const { onSidebar, currentView } = props;
    const { navigationMobileLinks } = configState;
    const isLargeDesktop = breakpointsState.largeDesktop.isBiggerOrEq;

    if (isLargeDesktop === false && isHamburgerMenuOpen === false) {
        return null;
    }

    const content = (
        <HeaderMainLinkWrapper
            isLargeDesktop={isLargeDesktop === true}
            isSpecialLink={navigationMobileLinks === 'special-links'}
            isHamburgerMenuOpen={isHamburgerMenuOpen}
            data-test='header-main-link-wrapper'
        >
            {getHeaderItems().map((item: HeaderItemType, idx: number) => {
                if ((onSidebar === true && item.onSidebar === false) || item.isHidden === true) {
                    return;
                }

                if (item.action.type === 'onclick') {
                    const onItemClick = item.action.onClick;
                    const dataTestAttr = `${item.action.dataTest}-link`;
                    return (
                        <HeaderStyledStaticOnClick
                            variant={configState.componentVariants.navigationVariant}
                            onClick={(): void => {
                                onItemClick();
                                appLayoutsState.overlay.toggleMenu();
                            }}
                            data-test={dataTestAttr}
                            key={idx}
                        >
                            {item.icon(isActive(item, currentView))}
                            {item.label}
                        </HeaderStyledStaticOnClick>
                    );
                } else if (item.action.type === 'redirect-outside') {
                    const { href } = item.action;
                    return (
                        <HeaderStyledStaticLink
                            variant={configState.componentVariants.navigationVariant}
                            onClick={(): void => {
                                fireGtmTag(href);
                                appLayoutsState.overlay.toggleMenu();
                            }}
                            dataTest={`${item.action.dataTest}`}
                            key={idx}
                            to={href}
                        >
                            {item.icon(isActive(item, currentView))}
                            {item.label}
                        </HeaderStyledStaticLink>
                    );
                } else {
                    const to = item.action.to;
                    const dataTestFromItem = item.action.dataTest ?? null;
                    const actionRoute = to?.name;

                    const dataTest =
                        dataTestFromItem === null
                            ? actionRoute === 'casino' && to.game === 'bingo'
                                ? 'bingo'
                                : actionRoute
                            : dataTestFromItem;

                    return (
                        <HeaderStyledLink
                            variant={configState.componentVariants.navigationVariant}
                            onClick={(): void => {
                                fireGtmTag(actionRoute);
                                appLayoutsState.overlay.toggleMenu();
                            }}
                            key={idx}
                            to={to}
                            isActive={isActive(item, currentView)}
                            dataTest={dataTest}
                        >
                            {item.icon(isActive(item, currentView))}
                            {item.label}
                        </HeaderStyledLink>
                    );
                }
            })}
        </HeaderMainLinkWrapper>
    );
    if (isLargeDesktop === false && isHamburgerMenuOpen === true) {
        return content;
    }

    return <LinksScrollableWrapper languageWidth={props.languageWidth}>{content}</LinksScrollableWrapper>;
});
